import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Button, Chip, Tab, Tabs, Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useDialog } from 'common/DialogProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import SkillCategoryDrawer from './SkillCategoryDrawer';
import SkillDrawer from './SkillDrawer';
import SkillLevelDrawer from './SkillLevelDrawer';

const SkillsPage: React.FC = () => {
  const { employeeApi }: Api = useApi();
  const hrApi = new HRApiWrapper(employeeApi);
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();

  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [skillDrawerOpen, setSkillDrawerOpen] = useState(false);
  const [levelDrawerOpen, setLevelDrawerOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedSkill, setSelectedSkill] = useState<any>(null);
  const [selectedLevel, setSelectedLevel] = useState<any>(null);

  const [errors] = useState({});
  const [paginationModel, setPaginationModel] = usePaginationPreference('hrSkills', 25);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, skillRes, levelRes] = await Promise.all([
        hrApi.getSkillCategories(),
        hrApi.getSkills('1', 1, 1000),
        hrApi.getSkillLevels(),
      ]);
      setCategories(catRes.data?.data ?? catRes.data ?? []);
      setSkills(skillRes.data?.data?.queryResult ?? skillRes.data?.data ?? []);
      setLevels(levelRes.data?.data ?? levelRes.data ?? []);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, []);

  const confirmDelete = (label: string, onConfirm: () => void) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => { await onConfirm(); closeDialog(dialogId); },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  // ─── Categories ────────────────────────────────────────────────────────────

  const categoryColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: intl.get('hr.skill.category.name'), flex: 1 },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { setSelectedCategory(params.row); setCategoryDrawerOpen(true); }}
          onDelete={() => confirmDelete(params.row.name, async () => {
            await hrApi.deleteSkillCategory('1', params.row.id);
            fetchAll();
          })}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  // ─── Skills ────────────────────────────────────────────────────────────────

  const skillColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: intl.get('hr.skill.name'), flex: 1 },
    {
      field: 'category', headerName: intl.get('hr.skill.category'), width: 180,
      valueGetter: (_: any, row: any) => row.category?.name ?? '',
    },
    {
      field: 'isSoft', headerName: intl.get('hr.skill.isSoft'), width: 120,
      renderCell: (params) => params.value
        ? <Chip label={intl.get('hr.skill.soft')} size="small" color="info" />
        : <Chip label={intl.get('hr.skill.technical')} size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { setSelectedSkill(params.row); setSkillDrawerOpen(true); }}
          onDelete={() => confirmDelete(params.row.name, async () => {
            await hrApi.deleteSkill('1', params.row.id);
            fetchAll();
          })}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  // ─── Levels ────────────────────────────────────────────────────────────────

  const levelColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: intl.get('hr.skill.level.name'), flex: 1 },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { setSelectedLevel(params.row); setLevelDrawerOpen(true); }}
          onDelete={() => confirmDelete(params.row.name, async () => {
            await hrApi.deleteSkillLevel('1', params.row.id);
            fetchAll();
          })}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  const tabData = [
    { label: intl.get('hr.skills.tab.categories'), rows: categories, columns: categoryColumns, onAdd: () => { setSelectedCategory(null); setCategoryDrawerOpen(true); } },
    { label: intl.get('hr.skills.tab.skills'), rows: skills, columns: skillColumns, onAdd: () => { setSelectedSkill(null); setSkillDrawerOpen(true); } },
    { label: intl.get('hr.skills.tab.levels'), rows: levels, columns: levelColumns, onAdd: () => { setSelectedLevel(null); setLevelDrawerOpen(true); } },
  ];

  const current = tabData[tab];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
          {intl.get('mainNavigation.humanResources.training')}
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            {tabData.map((t, i) => <Tab key={i} label={t.label} />)}
          </Tabs>
          <Button variant="contained" startIcon={<AddIcon />} onClick={current.onAdd}>
            {intl.get('common.add')}
          </Button>
        </Box>
        {loading ? <Spinner /> : (
          <Box style={{ width: '100%', display: 'flex' }}>
            <DataGrid
              rows={current.rows}
              columns={current.columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              sortingMode="client"
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      <SkillCategoryDrawer
        open={categoryDrawerOpen}
        onClose={() => setCategoryDrawerOpen(false)}
        onSave={async (data) => { selectedCategory ? await hrApi.updateSkillCategory('1', selectedCategory.id, data) : await hrApi.createSkillCategory('1', data); setCategoryDrawerOpen(false); fetchAll(); }}
        item={selectedCategory}
      />
      <SkillDrawer
        open={skillDrawerOpen}
        onClose={() => setSkillDrawerOpen(false)}
        onSave={async (data) => { selectedSkill ? await hrApi.updateSkill('1', selectedSkill.id, data) : await hrApi.createSkill('1', data); setSkillDrawerOpen(false); fetchAll(); }}
        item={selectedSkill}
        categories={categories}
      />
      <SkillLevelDrawer
        open={levelDrawerOpen}
        onClose={() => setLevelDrawerOpen(false)}
        onSave={async (data) => { selectedLevel ? await hrApi.updateSkillLevel('1', selectedLevel.id, data) : await hrApi.createSkillLevel('1', data); setLevelDrawerOpen(false); fetchAll(); }}
        item={selectedLevel}
      />
    </Card>
  );
};

export default SkillsPage;
